package com.evoapps.evoshift.domain.usecase.start

import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.StartPathRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.map
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes

/**
 * SH-219: the steps of a new owner's start path ("first roster in ten minutes"), in order. [id] is
 * what `workplaces/{id}/start_path/state` keeps for a skipped step (never rename one).
 */
enum class StartStep(val id: String) {
    BASICS("basics"),
    SHIFT_TEMPLATES("shift_templates"),
    WEEK("week"),
    TEAM("team"),
    FIRST_ROSTER("first_roster"),
    PUBLISH("publish");

    companion object {
        fun of(id: String): StartStep? = entries.firstOrNull { it.id == id }
    }
}

enum class StepState { DONE, SKIPPED, TO_DO }

/** How far the month the path works on has come: none yet, demand only, no one assigned, people assigned. */
enum class FirstRosterStage { NONE, SKELETON, UNASSIGNED, ASSIGNED }

/**
 * SH-219: what the workplace's data says about each step, never a click: an address (or a place on
 * the map), [shiftTemplates] defined, a week whose days have shifts, [teamSize] active people besides
 * the owner, the first month's [rosterStage] ([rosterId]: the earliest month not published yet) and
 * a published month ([publishedRosterId], the latest).
 */
data class StartFacts(
    val hasBasics: Boolean = false,
    val shiftTemplates: Int = 0,
    val hasWeek: Boolean = false,
    val teamSize: Int = 0,
    val rosterStage: FirstRosterStage = FirstRosterStage.NONE,
    val rosterId: String? = null,
    val publishedRosterId: String? = null
) {
    val hasPublished: Boolean get() = publishedRosterId != null

    fun isDone(step: StartStep): Boolean = when (step) {
        StartStep.BASICS -> hasBasics
        StartStep.SHIFT_TEMPLATES -> shiftTemplates > 0
        StartStep.WEEK -> hasWeek
        StartStep.TEAM -> teamSize > 0
        StartStep.FIRST_ROSTER -> rosterStage == FirstRosterStage.ASSIGNED || hasPublished
        StartStep.PUBLISH -> hasPublished
    }
}

/**
 * SH-219: what the owner chose on the path, kept per workplace (`workplaces/{id}/start_path/state`,
 * the owner's only): the steps skipped, the card dismissed, when the path started and when the first
 * month was published (the "time to first publish" metric is sent once, then this is set).
 */
data class StartPathProgress(
    val skipped: Set<StartStep> = emptySet(),
    val dismissed: Boolean = false,
    val startedAt: String? = null,
    val firstPublishedAt: String? = null
)

/** SH-219: the start path as the card shows it: each step done (from the data), skipped or still to do. */
data class StartPath(val facts: StartFacts, val progress: StartPathProgress = StartPathProgress()) {
    fun state(step: StartStep): StepState = when {
        facts.isDone(step) -> StepState.DONE
        step in progress.skipped -> StepState.SKIPPED
        else -> StepState.TO_DO
    }

    val doneCount: Int get() = StartStep.entries.count { state(it) == StepState.DONE }

    /** The first step still to do (the card's "Continue"); null once each is done or skipped. */
    val next: StartStep? get() = StartStep.entries.firstOrNull { state(it) == StepState.TO_DO }

    /** Every step done or skipped: the card may be dismissed. */
    val canDismiss: Boolean get() = next == null

    val allDone: Boolean get() = StartStep.entries.all { facts.isDone(it) }

    /**
     * Shown until dismissed, except at a workplace that was set up before the path existed (every
     * step done from the data and the path never started): no card for established owners.
     */
    val shows: Boolean get() = !progress.dismissed && !(allDone && progress.startedAt == null)

    companion object {
        val STEPS: Int = StartStep.entries.size
    }
}

/**
 * SH-219: [StartFacts] of a workplace, again whenever its rosters change (a month created or
 * published elsewhere); the templates, the week and the team are read with each change and on
 * [facts] (the dashboard reads again when the owner comes back from a step).
 */
class ObserveStartFactsUseCase(
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val rosterRepository: RosterRepository,
    private val shiftRepository: ShiftRepository
) {
    operator fun invoke(workplaceId: String): Flow<StartFacts> =
        rosterRepository.observeRosters(workplaceId).catch { emit(emptyList()) }.map { rosters -> facts(workplaceId, rosters) }

    suspend fun facts(workplaceId: String, rosters: List<Roster>): StartFacts {
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrNull()
        val templates = workplaceRepository.getShiftTemplates(workplaceId).getOrNull().orEmpty()
        val week = workplace?.settings?.defaultWeeklySkeleton.orEmpty()
        // A week counts once one of its days has a day template with shifts in it.
        val hasWeek = week.isNotEmpty() && workplaceRepository.getDayTemplates(workplaceId).getOrNull()
            ?.let { days -> week.values.any { id -> days.any { it.id == id && it.shiftTemplateIds.isNotEmpty() } } }
            ?: week.isNotEmpty()
        val team = userRepository.getAssignableEmployees(workplaceId).getOrNull().orEmpty()
            .filter { it.uid != workplace?.ownerId && it.engagements[workplaceId]?.isActive == true }
        val published = rosters.filter { it.status in PUBLISHED }
        val first = rosters.filter { it.status == RosterStatus.SKELETON || it.status == RosterStatus.DRAFT }
            .minByOrNull { it.year * 100 + it.month }
        val stage = when {
            first == null -> FirstRosterStage.NONE
            first.status == RosterStatus.SKELETON -> FirstRosterStage.SKELETON
            shiftRepository.getShiftsForRoster(first.id).getOrNull().orEmpty().any { it.userId != null } -> FirstRosterStage.ASSIGNED
            else -> FirstRosterStage.UNASSIGNED
        }
        return StartFacts(
            hasBasics = workplace != null && (workplace.address.isNotBlank() || workplace.location != null),
            shiftTemplates = templates.size,
            hasWeek = hasWeek,
            teamSize = team.size,
            rosterStage = stage,
            rosterId = first?.id,
            publishedRosterId = published.maxByOrNull { it.year * 100 + it.month }?.id
        )
    }

    private companion object {
        val PUBLISHED = setOf(RosterStatus.PUBLISHED, RosterStatus.LOCKED, RosterStatus.ARCHIVED)
    }
}

/** SH-219: reads and keeps the owner's [StartPathProgress] (none yet: a fresh one). */
class StartPathProgressUseCase(private val repository: StartPathRepository) {
    suspend fun load(workplaceId: String): Result<StartPathProgress> =
        repository.get(workplaceId).map { it ?: StartPathProgress() }

    suspend fun save(workplaceId: String, progress: StartPathProgress): Result<Unit> = repository.save(workplaceId, progress)
}

/**
 * SH-219: the anonymous "time to first publish" metric: the time from the start path's start to the
 * first published month, as a coarse bucket (an identifier, never the exact time).
 */
object FirstPublishTime {
    fun bucket(elapsed: Duration): String = when {
        elapsed < 10.minutes -> "under_10_min"
        elapsed < 30.minutes -> "10_to_30_min"
        elapsed < 1.hours -> "30_to_60_min"
        elapsed < 1.days -> "1_to_24_h"
        elapsed < 7.days -> "1_to_7_days"
        else -> "over_7_days"
    }
}
